import { PrebidLoader } from "./PrebidLoader";
import { registerAdUnits } from "./registerAdUnits"; 
import { HealthChecker } from "./HealthChecker";
import { prebidConfig, prebidRatesReady } from "./prebidConfig";

export class PrebidInitializer {
  constructor() {
    this.pbjs = window.mypbjs || { que: [] };
    window.mypbjs = this.pbjs;
    this.initialized = false;
    this.adUnitsRegistered = false;
    this.adUnitListeners = []; 

     this.init();
  }
  
  async init() {
    if (this.initialized) return;
    
    const loaded = await PrebidLoader.load();
    if (!loaded) {
      console.error("[PrebidInitializer] Failed to load Prebid.js");
      return;
    }
    
    await this.setConfiguration();
    
    const pbsHealthy = await HealthChecker.checkPrebidServer();
    if (!pbsHealthy) {
      this.updateForClientSideBidding();
    }
    
    await this.initializeUserIds();

    //Register ad units and notify subscribers when done
    const adUnitsRegistered = await this.registerAdUnits();
    if (adUnitsRegistered) {
      this.adUnitsRegistered = true;
      this.notifyAdUnitSubscribers();
    } else {
      console.warn("[PrebidInitializer] Ad unit registration failed.");
    }
    
    this.initialized = true;
  }
  
  async setConfiguration() {
    this.pbjs.que = this.pbjs.que || [];   // defensive

    const WAIT_MS = 200;
    try {
      await Promise.race([
        prebidRatesReady,
        new Promise(resolve => setTimeout(resolve, WAIT_MS))
      ]);
    } catch (e) {
      // ignore; fallback USD only is fine
    }

    this.pbjs.que.push(() => {
      try {
        this.pbjs.setConfig(prebidConfig);
        // optional dev log:
        // console.info('[PrebidInitializer] mypbjs.setConfig applied', prebidConfig.currency);
      } catch (err) {
        console.error('[PrebidInitializer] mypbjs.setConfig failed', err);
      }
    });

    // optional: reapply currency if rates arrive slightly later
    prebidRatesReady.then(ok => {
      if (ok && this.pbjs && Array.isArray(this.pbjs.que)) {
        this.pbjs.que.push(() => {
          try { this.pbjs.setConfig({ currency: prebidConfig.currency }); } catch (e) {}
        });
      }
    });
  }

  
  updateForClientSideBidding() {
    this.pbjs.que.push(() => {
      this.pbjs.setConfig({ s2sConfig: { enabled: false } });
      //console.warn("[PrebidInitializer] Switched to client-side bidding due to PBS failure.");
    });
  }
  
    async initializeUserIds() {
      return new Promise(resolve => {
        this.pbjs.que.push(resolve);
      });
    }

  async registerAdUnits() {
    return await registerAdUnits();
  }

  // Allow components (like `usePrebid`) to subscribe to ad unit updates
  subscribeToAdUnits(callback) {
    this.adUnitListeners.push(callback);

    // If ad units are already registered, notify immediately
    if (this.adUnitsRegistered) {
      callback(true);
    }

    return () => {
      this.adUnitListeners = this.adUnitListeners.filter(listener => listener !== callback);
    };
  }

  notifyAdUnitSubscribers() {
    this.adUnitListeners.forEach(callback => callback(true));
  }
}

//Export as a Singleton
const prebidInstance = new PrebidInitializer();
export default prebidInstance;
